import { useEffect, useState, useMemo, useCallback } from 'react';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Badge, Avatar, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { getTeamOwnerPlan } from '../../lib/plan';
import LimitReachedModal from '../../components/workspace/LimitReachedModal';
import { useIsMobile } from '../../lib/useBreakpoint';

const ROLE_TO_BADGE = {
  owner:    'navy',
  admin:    'info',
  reviewer: 'warn',
  staff:    'info',
  client:   'neutral',
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)     return Math.floor(diff / 3600) + 'h ago';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd ago';
  return new Date(iso).toLocaleDateString();
}

export default function MembersPanel() {
  const { team, role, isOwner, refreshCounts } = useWorkspace();
  const confirm = useConfirm();
  const toast = useToast();
  const isMobile = useIsMobile();
  const canInvite  = role?.can_invite || isOwner;
  const canManage  = isOwner || role?.slug === 'admin';

  const [members, setMembers]     = useState([]);
  const [roles, setRoles]         = useState([]);
  const [invites, setInvites]     = useState([]);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [actingId, setActingId]   = useState(null);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);

  // Phase 3A: limit modal state (member limit hit)
  const [limitHit, setLimitHit] = useState(null);

  const loadAll = useCallback(async () => {
    if (!team?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [membersRes, rolesRes, invitesRes] = await Promise.all([
        supabase
          .from('team_members')
          .select('*, team_roles(id, name, slug, is_staff)')
          .eq('team_id', team.id),
        supabase
          .from('team_roles')
          .select('*')
          .eq('team_id', team.id)
          .order('is_staff', { ascending: false })
          .order('name', { ascending: true }),
        supabase
          .from('team_invites')
          .select('*, team_roles(id, name, slug)')
          .eq('team_id', team.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false }),
      ]);
      if (membersRes.error) throw membersRes.error;
      setMembers(membersRes.data || []);
      setRoles(rolesRes.data || []);
      setInvites(invitesRes.data || []);
    } catch (err) {
      console.error('Members load failed:', err);
      setError(err.message || 'Failed to load members.');
    } finally {
      setLoading(false);
    }
  }, [team?.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Role count summary ───────────────────────────────────────────────
  const roleSummary = useMemo(() => {
    const byRole = {};
    members.forEach(m => {
      const slug = m.team_roles?.slug || 'unknown';
      const name = m.is_owner ? 'Owner' : (m.team_roles?.name || 'Member');
      if (!byRole[slug]) byRole[slug] = { name, slug, count: 0, isStaff: m.team_roles?.is_staff };
      byRole[slug].count++;
    });
    // Push owners first, then staff, then clients
    return Object.values(byRole).sort((a, b) => {
      if (a.slug === 'owner') return -1;
      if (b.slug === 'owner') return 1;
      if (a.isStaff && !b.isStaff) return -1;
      if (!a.isStaff && b.isStaff) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [members]);

  // ── Filtered list ────────────────────────────────────────────────────
  const visibleMembers = useMemo(() => {
    let xs = members;
    if (roleFilter !== 'all') {
      xs = xs.filter(m => (m.team_roles?.slug || '') === roleFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(m => {
        const name = [m.first_name, m.last_name].filter(Boolean).join(' ').toLowerCase();
        return name.includes(q) || (m.email || '').toLowerCase().includes(q);
      });
    }
    return xs.sort((a, b) => {
      // Owners first, then by name
      if (a.is_owner && !b.is_owner) return -1;
      if (!a.is_owner && b.is_owner) return 1;
      const an = [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || '';
      const bn = [b.first_name, b.last_name].filter(Boolean).join(' ') || b.email || '';
      return an.localeCompare(bn);
    });
  }, [members, roleFilter, search]);

  // ── Change a member's role ────────────────────────────────────────────
  const changeRole = async (member, newRoleId) => {
    if (actingId) return;
    if (member.role_id === newRoleId) return;

    // Owner protection: can't demote the last owner
    const newRole = roles.find(r => r.id === newRoleId);
    const currentRole = member.team_roles;
    if (currentRole?.slug === 'owner' && newRole?.slug !== 'owner') {
      const ownerCount = members.filter(m => m.team_roles?.slug === 'owner').length;
      if (ownerCount <= 1) {
        toast.error('You can\'t demote the last owner. Promote someone else first.');
        return;
      }
    }

    setActingId(member.id);
    try {
      const updates = { role_id: newRoleId };
      // is_owner flag follows the owner role
      if (newRole?.slug === 'owner') updates.is_owner = true;
      else if (currentRole?.slug === 'owner') updates.is_owner = false;

      const { error: updErr } = await supabase
        .from('team_members')
        .update(updates)
        .eq('id', member.id);
      if (updErr) throw updErr;

      setMembers(members.map(m => m.id === member.id
        ? { ...m, ...updates, team_roles: newRole }
        : m
      ));
    } catch (err) {
      console.error('Role change failed:', err);
      toast.error('Could not change role: ' + (err.message || 'unknown error'));
    } finally {
      setActingId(null);
    }
  };

  // ── Remove a member ──────────────────────────────────────────────────
  const removeMember = async (member) => {
    if (actingId) return;
    if (member.is_owner) {
      const ownerCount = members.filter(m => m.is_owner).length;
      if (ownerCount <= 1) {
        toast.error('You can\'t remove the last owner.');
        return;
      }
    }
    const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email;
    const r = await confirm({
      title: 'Remove member',
      message: `Remove ${memberName} from this workspace? They'll lose access immediately.`,
      confirmLabel: 'Remove',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;

    setActingId(member.id);
    try {
      const { error: delErr } = await supabase
        .from('team_members')
        .delete()
        .eq('id', member.id);
      if (delErr) throw delErr;
      setMembers(members.filter(m => m.id !== member.id));
      refreshCounts();
    } catch (err) {
      console.error('Remove failed:', err);
      toast.error('Could not remove: ' + (err.message || 'unknown error'));
    } finally {
      setActingId(null);
    }
  };

  // ── Cancel a pending invite ──────────────────────────────────────────
  const cancelInvite = async (inv) => {
    if (actingId) return;
    const r = await confirm({
      title: 'Cancel invite',
      message: `Cancel the pending invite to ${inv.email}?`,
      confirmLabel: 'Cancel invite',
      cancelLabel: 'Keep invite',
    });
    if (!r.confirmed) return;
    setActingId(inv.id);
    try {
      const { error: delErr } = await supabase
        .from('team_invites')
        .delete()
        .eq('id', inv.id);
      if (delErr) throw delErr;
      setInvites(invites.filter(i => i.id !== inv.id));
    } catch (err) {
      toast.error('Could not cancel invite: ' + (err.message || 'unknown error'));
    } finally {
      setActingId(null);
    }
  };

  // ── Invite handler (passed to modal) ─────────────────────────────────
  const onInvited = (newInvite) => {
    // newInvite carries the REAL team_invites.id from the server response so
    // cancel has a valid UUID. If the id couldn't be determined, refetch
    // instead of inserting a placeholder row (which would break cancel).
    if (newInvite) {
      setInvites(prev => [newInvite, ...prev]);
    } else {
      loadAll();
    }
    setInviteOpen(false);
    refreshCounts();
  };

  // ────────────────────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title="Members"
        subtitle={`${members.length} member${members.length === 1 ? '' : 's'} · ${invites.length} pending invite${invites.length === 1 ? '' : 's'}`}
        actions={canInvite && (
          <Btn variant="primary" size="md" icon={Icon.plus} onClick={async () => {
            // Phase 3A: pre-flight member-limit check.
            // Only Free plan has a cap; paid plans are unlimited.
            // Clients (non-staff) don't count toward the limit.
            try {
              const { plan, limits } = await getTeamOwnerPlan(team.id);
              if (plan === 'free') {
                const staffCount = members.filter(m => m.team_roles?.is_staff !== false).length;
                if (staffCount >= limits.staff_members) {
                  setLimitHit({
                    type: 'members',
                    used: staffCount,
                    limit: limits.staff_members,
                    plan,
                  });
                  return;
                }
              }
            } catch (err) {
              console.warn('[MembersPanel] member-limit pre-flight failed:', err);
              // Don't block invite on check failure — let backend enforce
            }
            setInviteOpen(true);
          }}>
            Invite member
          </Btn>
        )}
      />

      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      {/* Role summary cards */}
      {roleSummary.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? 'repeat(2, 1fr)'
            : 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: isMobile ? 10 : 12, marginBottom: 24,
        }}>
          {roleSummary.map((r) => (
            <Card key={r.slug} style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: r.isStaff ? 'rgba(59,130,246,0.1)' : 'var(--accent-bg)',
                  color: r.isStaff ? '#3b82f6' : 'var(--accent-600)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon.users size={14} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{r.name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                    {r.isStaff ? 'Staff role' : 'Client role'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)' }}>
                  {r.count}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  member{r.count === 1 ? '' : 's'}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pending invites section */}
      {invites.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface-2)',
          }}>
            <Icon.inbox size={14} stroke="var(--accent-600)" />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              Pending invites ({invites.length})
            </span>
          </div>
          {invites.map((inv) => (
            <div key={inv.id} style={{
              padding: '12px 16px',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 10 : 12,
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                flex: isMobile ? undefined : 1, minWidth: 0,
              }}>
                <Avatar name={inv.email} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {inv.email}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                    Invited as {inv.team_roles?.name || 'Member'} · {timeAgo(inv.created_at)}
                  </div>
                </div>
                {isMobile && <Badge variant="warn" dot>Pending</Badge>}
              </div>
              {!isMobile && <Badge variant="warn" dot>Pending</Badge>}
              {canInvite && (
                <Btn
                  variant="outline"
                  size="sm"
                  onClick={() => cancelInvite(inv)}
                  disabled={actingId === inv.id}
                  style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
                >
                  Cancel invite
                </Btn>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Member list */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: isMobile ? '10px 12px' : '12px 16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 8 : 10,
          alignItems: isMobile ? 'stretch' : 'center',
          borderBottom: '1px solid var(--border)',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
        }}>
          <div style={{ width: isMobile ? '100%' : 280 }}>
            <Input
              icon={Icon.search}
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={isMobile ? undefined : { height: 32, fontSize: 12.5 }}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="mi-focusable"
            style={{
              height: isMobile ? 44 : 32, padding: '0 10px',
              fontSize: isMobile ? 14 : 12.5,
              border: '1px solid var(--border)',
              background: 'var(--surface)', color: 'var(--text)',
              borderRadius: 8, fontFamily: 'inherit', cursor: 'pointer',
              width: isMobile ? '100%' : undefined,
            }}
          >
            <option value="all">All roles</option>
            {roles.map(r => <option key={r.id} value={r.slug}>{r.name}</option>)}
          </select>
          {!isMobile && <div style={{ flex: 1 }} />}
          <span style={{
            fontSize: 12, color: 'var(--text-3)',
            alignSelf: isMobile ? 'flex-start' : 'auto',
          }}>
            Showing {visibleMembers.length} of {members.length}
          </span>
        </div>

        {/* Header — desktop only (cards have inline labels on mobile) */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(240px, 2fr) 160px 110px 100px 60px',
            gap: 12, padding: '10px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 11, fontWeight: 600, color: 'var(--text-3)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <div>Member</div>
            <div>Role</div>
            <div>Status</div>
            <div>Language</div>
            <div></div>
          </div>
        )}

        {loading && members.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Spinner size={20} color="var(--accent)" />
          </div>
        ) : visibleMembers.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            No members match your filters.
          </div>
        ) : (
          visibleMembers.map((m) => {
            const memberName = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '(unknown)';
            const roleSlug = (m.team_roles?.slug || '').toLowerCase();
            const isActing = actingId === m.id;

            // Mobile card layout
            if (isMobile) {
              return (
                <div key={m.id} style={{
                  padding: '12px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--surface)',
                }}>
                  {/* Top row: avatar + name + role badge (right) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <Avatar name={memberName} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--text)',
                        display: 'flex', alignItems: 'center', gap: 6,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {memberName}
                        {m.is_owner && <Badge variant="navy" size="xs">Owner</Badge>}
                      </div>
                      <div style={{
                        fontSize: 11.5, color: 'var(--text-3)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {m.email}
                      </div>
                    </div>
                  </div>

                  {/* Status + language line */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10,
                  }}>
                    <Badge variant="ok" size="xs" dot>Active</Badge>
                    <span>·</span>
                    <span>{m.language === 'es' ? 'Español' : 'English'}</span>
                  </div>

                  {/* Role + remove row */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {canManage && !m.is_owner ? (
                      <select
                        value={m.role_id || ''}
                        onChange={(e) => changeRole(m, e.target.value)}
                        disabled={isActing}
                        className="mi-focusable"
                        style={{
                          flex: 1, minWidth: 0, height: 36, padding: '0 10px',
                          fontSize: 14, fontFamily: 'inherit',
                          border: '1px solid var(--border)',
                          background: 'var(--surface)', color: 'var(--text)',
                          borderRadius: 8, cursor: isActing ? 'wait' : 'pointer',
                        }}
                      >
                        {roles.filter(r => r.slug !== 'owner').map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ flex: 1 }}>
                        <Badge variant={ROLE_TO_BADGE[roleSlug] || 'neutral'}>
                          {m.is_owner ? 'Owner' : (m.team_roles?.name || 'Member')}
                        </Badge>
                      </div>
                    )}
                    {canManage && !m.is_owner && (
                      <Btn
                        variant="outline"
                        size="sm"
                        icon={isActing ? null : Icon.trash}
                        onClick={() => removeMember(m)}
                        disabled={isActing}
                        title="Remove member"
                        style={{ color: 'var(--danger)' }}
                      >
                        {isActing ? <Spinner size={12} color="var(--danger)" /> : 'Remove'}
                      </Btn>
                    )}
                  </div>
                </div>
              );
            }

            // Desktop grid row (unchanged)
            return (
              <div key={m.id} style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(240px, 2fr) 160px 110px 100px 60px',
                gap: 12, padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Avatar name={memberName} size={32} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: 'var(--text)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      {memberName}
                      {m.is_owner && <Badge variant="navy" size="xs">Owner</Badge>}
                    </div>
                    <div style={{
                      fontSize: 11.5, color: 'var(--text-3)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {m.email}
                    </div>
                  </div>
                </div>

                {/* Role select (or badge if not manageable) */}
                <div>
                  {canManage && !m.is_owner ? (
                    <select
                      value={m.role_id || ''}
                      onChange={(e) => changeRole(m, e.target.value)}
                      disabled={isActing}
                      className="mi-focusable"
                      style={{
                        width: '100%', height: 30, padding: '0 8px',
                        fontSize: 12, fontFamily: 'inherit',
                        border: '1px solid var(--border)',
                        background: 'var(--surface)', color: 'var(--text)',
                        borderRadius: 6, cursor: isActing ? 'wait' : 'pointer',
                      }}
                    >
                      {roles.filter(r => r.slug !== 'owner').map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Badge variant={ROLE_TO_BADGE[roleSlug] || 'neutral'}>
                      {m.is_owner ? 'Owner' : (m.team_roles?.name || 'Member')}
                    </Badge>
                  )}
                </div>

                <div>
                  <Badge variant="ok" dot>Active</Badge>
                </div>

                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {m.language === 'es' ? 'Español' : 'English'}
                </div>

                <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  {canManage && !m.is_owner && (
                    <button
                      type="button"
                      onClick={() => removeMember(m)}
                      disabled={isActing}
                      title="Remove member"
                      style={{
                        width: 28, height: 28, border: 'none', background: 'transparent',
                        borderRadius: 6, cursor: isActing ? 'wait' : 'pointer',
                        color: 'var(--text-3)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onMouseEnter={(e) => { if (!isActing) { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'var(--danger-bg)'; } }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      {isActing ? <Spinner size={12} /> : <Icon.trash size={14} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </Card>

      {inviteOpen && (
        <InviteModal
          team={team}
          roles={roles}
          onClose={() => setInviteOpen(false)}
          onInvited={onInvited}
        />
      )}

      {limitHit && (
        <LimitReachedModal
          type={limitHit.type}
          used={limitHit.used}
          limit={limitHit.limit}
          plan={limitHit.plan}
          onClose={() => setLimitHit(null)}
          onUpgrade={() => {
            setLimitHit(null);
            window.location.href = '/account';
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Invite modal
// ────────────────────────────────────────────────────────────────────────────
function InviteModal({ team, roles, onClose, onInvited }) {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(roles.find(r => r.slug === 'client')?.id || roles[0]?.id || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!email.trim()) {
      setError('Please enter an email address.');
      return;
    }
    if (!roleId) {
      setError('Please choose a role.');
      return;
    }
    if (!team?.id) {
      setError('Workspace context is loading. Please close and retry in a moment.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        team_id: team.id,
        email: email.trim().toLowerCase(),
        role_id: roleId,
      };
      console.log('[Invite] Sending:', payload);
      const result = await api('/api/invite-member', payload);
      console.log('[Invite] Response:', result);

      // Build the optimistic list row using the REAL team_invites.id the
      // backend returns as `invite_id` — cancel deletes by this UUID. (The old
      // code read `inviteId`/`invite`, which the API never sends, so it always
      // fell back to a `tmp-<ts>` id and cancel hit "invalid input syntax for
      // type uuid".) If no id comes back, pass null so the parent refetches.
      const invitedRole = roles.find(r => r.id === roleId);
      onInvited(result?.invite_id ? {
        id: result.invite_id,
        email: email.trim().toLowerCase(),
        team_id: team.id,
        role_id: roleId,
        team_roles: invitedRole,
        created_at: new Date().toISOString(),
        status: 'pending',
      } : null);
    } catch (err) {
      console.error('Invite failed:', err);
      setError(err.message || 'Could not send invite.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,15,46,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card style={{
        padding: isMobile ? 20 : 28,
        maxWidth: isMobile ? 'none' : 440,
        width: '100%',
        height: isMobile ? '100dvh' : 'auto',
        borderRadius: isMobile ? 0 : 'var(--r-lg)',
        display: 'flex', flexDirection: 'column',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
            Invite to {team?.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 40, height: 40,
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: 'var(--text-3)', borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon.x size={20} />
          </button>
        </div>

        {error && <ErrorBanner message={error} style={{ marginBottom: 12 }} />}

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
          Email address
        </label>
        <Input
          icon={Icon.mail}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@example.com"
          disabled={submitting}
          style={{ marginBottom: 14 }}
        />

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
          Role
        </label>
        <select
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          disabled={submitting}
          className="mi-focusable"
          style={{
            width: '100%',
            height: isMobile ? 44 : 40,
            padding: '0 12px',
            fontSize: 13.5, fontFamily: 'inherit',
            border: '1px solid var(--border)',
            background: 'var(--surface)', color: 'var(--text)',
            borderRadius: 'var(--r-md)', marginBottom: 20, cursor: 'pointer',
          }}
        >
          {roles.filter(r => r.slug !== 'owner').map(r => (
            <option key={r.id} value={r.id}>
              {r.name}{r.is_staff ? ' (Staff)' : ''}
            </option>
          ))}
        </select>

        <div style={{
          display: 'flex', gap: 8,
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          marginTop: isMobile ? 'auto' : 0,
          paddingTop: isMobile ? 16 : 0,
        }}>
          <Btn
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={submitting}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >
            Cancel
          </Btn>
          <Btn
            variant="primary"
            size="md"
            icon={submitting ? null : Icon.send}
            onClick={submit}
            disabled={submitting || !email.trim()}
            style={isMobile ? { flex: 2, justifyContent: 'center' } : undefined}
          >
            {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Sending…</> : 'Send invite'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
